import { defineStore } from 'pinia';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';
import { getAllColors } from '@/helpers/colors';

const colors = getAllColors();

const URL = 'v2/dashboard/display_metric';

export const useMetricDataStore = defineStore('metricData', {
    state: () => ({
        metricReportData: [],
        tableData: [],
        tableColumns: [],
        chartLabels: [],
        chartData: [],
        pieChartDataTypes: [],
        selectedPieChartDataTypes: [],
    }),
    actions: {
        async getMetricData(company_id, metric_id) {
            try {
                const response = await GlobalRequest.getRequest(URL, {
                    params: {
                        company_id,
                        metric_id,
                    },
                });
                if (response.statusCode === 200) {
                    this.metricReportData = response.data;
                }
                return response.data;
            } catch (error) {
                showToast({}, error?.data?.response ?? 'Failed to extract chart data');
            }
        },
        getTabularData(data) {
            if (data) {
                // we need to select only first row of data to set the table columns
                let firstRowData = data[0];
                // now extract label from each objects inside the values object in firstRowData
                // and set them as table columns by formatting them into field and headers
                this.tableColumns = firstRowData?.values?.map((obj) => {
                    return {
                        field: obj.type + ' ' + obj.label,
                        header: this.generateLabel(obj),
                    };
                });
                // now format the data
                // first column data is present in the outer row of each object while the rest data resides inside values object
                /**
                 * Metric Data Format                       Required Format
                 * [                                        [
                 *   {                                         {
                 *     "label": "Group A",                       "group_by": "Group A",
                 *     "group_by": "Group A",                    "Data A" : "numeric_data",
                 *     "values": [                               "Data B" : "numeric_data",
                 *       {                                       "Data C" : "numeric_data",
                 *         "label": "Data A",                  },
                 *         "type": "average",                  {
                 *         "value": "numeric_data"               "group_by": "Group B",
                 *       },                                      "Data A" : "numeric_data",
                 *       {                                       "Data B" : "numeric_data",
                 *         "label": "Data B",                    "Data C" : "numeric_data",
                 *         "type": "sum",                      },
                 *         "value": "numeric_data"             ..
                 *       },                                    ..
                 *       {                                     ..
                 *         "label": "Data C",               ]
                 *         "type": "min",
                 *         "value": "numeric_data"
                 *       },
                 *       ..
                 *       ..
                 *       ..
                 *     ]
                 *   },
                 *   ..
                 *   ..
                 * ]
                 */
                const tableData = data?.map((row) => {
                    // first label of every main row is the group by value
                    const newData = { group_by: row.label };
                    row?.values?.forEach((obj) => {
                        newData[obj.type + ' ' + obj.label] = obj.value?.toFixed(2);
                    });
                    return newData;
                });
                this.tableData = tableData;
                return this.tableData;
            }
            return [];
        },
        getDSTabularData(data) {
            if (data && Object.keys(data)?.length > 0) {
                // we need to select only first row of data to set the table columns
                let firstRowData = data[0];
                // now extract label from each objects inside the values object in firstRowData
                // and set them as table columns by formatting them into field and headers
                const DStableColumns = firstRowData?.values?.map((obj) => {
                    return {
                        field: obj.label,
                        header: this.capitalizeWords(obj.label),
                    };
                });
                // now format the data
                let tableData = data?.map((row) => {
                    // first label of every main row is the group by value
                    const newData = { group_by: row.label };
                    row?.values?.forEach((obj) => {
                        newData[obj.label] = obj.value ? parseFloat(obj.value)?.toFixed(2) : '-';
                    });
                    return newData;
                });
                // Filter out here
                tableData = tableData?.filter((dataObj) => Object.keys(dataObj).length > 1);
                return {
                    tableColumns: DStableColumns,
                    tableData,
                };
            }
            return null;
        },
        // since bar charts and line charts are similar, the same function is reused
        // only difference in the datasets is that line charts has property tension
        getBarChartDatasets(data, documentStyle, tension = false) {
            if (data) {
                // For the x-axis, we need to extract all the group by labels
                this.chartLabels = data?.map((obj) => obj.label?.toUpperCase());
                // now format the data
                // first column data is present in the outer row of each object while the rest data resides inside values object
                /**
                 * labels: ["Group A", "Group B", ...] :- length of array inside data === labels count
                 *
                 * Metric Data Format                       Required Format for datasets
                 * [                                        [
                 *   {                                         {
                 *     "label": "Group A",                       label: "Group A",
                 *     "group_by": "Group A",                    backgroundColor : ....,
                 *     "values": [                               borderColor : ....,
                 *       {                                       data : [numeric_data, numeric_data],
                 *         "label": "Data A",                  },
                 *         "type": "average",                  {
                 *         "value": "numeric_data"               label: "Group B",
                 *       },                                      backgroundColor : ....,
                 *       {                                       borderColor : ....,
                 *         "label": "Data B",                    data : [numeric_data, numeric_data],
                 *         "type": "sum",                      },
                 *         "value": "numeric_data"             ..
                 *       },                                    ..
                 *       {                                     ..
                 *         "label": "Data C",               ]
                 *         "type": "min",
                 *         "value": "numeric_data"
                 *       },
                 *       ..
                 *     ]
                 *   },
                 *   ..
                 * ]
                 */
                // Intially, make normal array of values only
                const chartValues = data?.map((item) => item.values);
                // extract first row and get all the possible different data available using each labels and types inside values
                const dataTypes = data[0]?.values?.map((obj) => {
                    return { label: obj.label, type: obj.type };
                });
                // create an array to store every datasets we will be creating for each type in dataTypes
                let chartData = [];

                // Now using dataTypes, we can filter out and align all the datasets collections in each sets using dataTypes
                dataTypes?.forEach((dataType, index) => {
                    const newData = {
                        label: this.generateLabel(dataType),
                        backgroundColor: documentStyle.getPropertyValue(this.applyColorsToColumns(index)),
                        borderColor: documentStyle.getPropertyValue(this.applyColorsToColumns(index)),
                        data: chartValues?.map((chartValue) => {
                            const barData = chartValue.find((obj) => obj.label === dataType.label && obj.type === dataType.type)?.value;
                            return barData;
                        }),
                    };
                    // if tension is true then, the value is set to 0.4 and added to newData property
                    // tension applies only in case of Line charts and in Bar charts, it doesnt apply
                    if (tension) {
                        newData.tension = 0.4;
                    }
                    chartData.push(newData);
                });
                this.chartData = chartData;
                return chartData;
            }
            return [];
        },
        getDSBarChartDatasets(data, documentStyle, tension = false) {
            if (data) {
                // For the x-axis, we need to extract all the group by labels
                const chartLabels = data?.map((obj) => obj.label?.toUpperCase());
                // now format the data
                // Intially, make normal array of values only
                const chartValues = data?.map((item) => item.values);
                // extract first row and get all the possible different data available using each labels and types inside values
                const dataTypes = data[0]?.values?.map((obj) => {
                    return { label: obj.label };
                });
                // create an array to store every datasets we will be creating for each type in dataTypes
                let chartData = [];

                // Now using dataTypes, we can filter out and align all the datasets collections in each sets using dataTypes
                dataTypes?.forEach((dataType, index) => {
                    const colorData = documentStyle.getPropertyValue(this.applyColorsToColumns(index));
                    const newData = {
                        label: this.generateLabel(dataType),
                        backgroundColor: colorData,
                        borderColor: colorData,
                        data: chartValues?.map((chartValue) => {
                            const barData = chartValue.find((obj) => obj.label === dataType.label)?.value;
                            return barData;
                        }),
                    };
                    // Filter out data with null values from array of new Data
                    // newData.data = newData.data?.filter((dataObj) => dataObj && dataObj !== null);
                    // if tension is true then, the value is set to 0.4 and added to newData property
                    // tension applies only in case of Line charts and in Bar charts, it doesnt apply
                    if (tension) {
                        newData.tension = 0.4;
                    }
                    // debugger;
                    chartData.push(newData);
                });

                return { chartLabels, chartData };
            }
            return null;
        },
        getPieChartDatasets(data, documentStyle, actualSize = 0) {
            if (data) {
                // For the x-axis, we need to extract all the group by labels
                this.chartLabels = data?.map((obj) => obj.label?.toUpperCase());
                /** now format the data
                 *  in context of pie chart, we expect every group to have only one datasets each,
                 * if there are multiple datasets then it will be a multi layered pie charts
                 * labels: ["Group A", "Group B", ...] :- length of array inside data === labels count
                 *
                 * Metric Data Format                       Required Format for datasets
                 * [                                        [
                 *   {                                         {
                 *     "label": "Group A",                       data : [numeric_data, numeric_data,..],
                 *     "group_by": "Group A",                    backgroundColor : ....,
                 *     "values": [                               hoverBackgroundColor : ....,
                 *       {                                     },
                 *         "label": "Data A",                  {
                 *         "type": "average",                   data : [numeric_data, numeric_data,..],
                 *         "value": "numeric_data"              backgroundColor : ....,
                 *       }                                      hoverBackgroundColor : ....,
                 *   },                                        },
                 *   {                                         ..
                 *     "label": "Group B",                     ..
                 *     "group_by": "Group A",               ]
                 *     "values": [
                 *       {
                 *         "label": "Data B",
                 *         "type": "average",
                 *         "value": "numeric_data"
                 *       }
                 *   },
                 */
                // Intially, make normal array of values only
                // const chartValues = data?.map((item) => item.values[0]);
                // console.log(chartValues);
                // console.log(dataTypes);
                // extract first row and get all the possible different data available using each labels inside values
                // we can use dataTypes as a dropdown functionality
                // this.pieChartDataTypes = data[0]?.values?.map((obj) => {
                //     return { label: obj.label, type: obj.type };
                // });
                this.pieChartDataTypes = data[0]?.values?.map((obj) => {
                    return { label: obj.label, type: obj.type };
                });
                // initially we will show all the pieChartDataTypes so we will put it as selectedPieChartDataTypes
                this.selectedPieChartDataTypes = this.pieChartDataTypes;
                // normally when you are choosing different datatypes in the multiselect option, changes in number/length of datatypes
                // can impact on the pie chart colors while setting up piechartcolorsets, so the actualSize is sent from the BaseChart everytime

                // const dataTypesCount = actualSize > 0 ? actualSize : this.pieChartDataTypes?.length;
                const dataTypesCount = actualSize > 0 ? actualSize : data?.length;

                // create an array to store every datasets we will be creating for each type in dataTypes

                // let chartData = [];

                // Now using dataTypes, we can filter out and align all the datasets collections in each sets using dataTypes
                // this.pieChartDataTypes.forEach((dataType) => {
                //     const newData = {
                //         label: this.generateLabel(dataType),
                //         backgroundColor: this.getPieChartColorSets(dataTypesCount, documentStyle),
                //         hoverBackgroundColor: this.getPieChartColorSets(dataTypesCount, documentStyle),
                //         // data: chartValues?.map((chartValue) => {
                //         //     const pieData = chartValue.find((obj) => obj.label === dataType.label && obj.type === dataType.type)?.value;
                //         //     return pieData;
                //         // }),
                //         data: chartValues?.find((obj) => obj.label === dataType.label && obj.type === dataType.type)?.value,
                //     };
                //     chartData.push(newData);
                // });

                const pieDataValues = data?.map((datum) => datum.values[0])?.map((obj) => obj.value);
                this.chartData = {
                    // label: data?.map((datum) => datum.label),
                    backgroundColor: this.getPieChartColorSets(dataTypesCount, documentStyle),
                    hoverBackgroundColor: this.getPieChartColorSets(dataTypesCount, documentStyle),
                    data: pieDataValues,
                };
                // send as array format
                return [this.chartData];
            }
            return [];
        },
        getPieChartColorSets(colorsCount, documentStyle) {
            let colors = [];
            for (let i = 0; i < colorsCount; i++) {
                colors.push(documentStyle.getPropertyValue(this.applyColorsToColumns(i)));
            }
            return colors;
        },

        applyColorsToColumns() {
            // get index among colors length
            // % colors?.length
            const colorIndex = Math.floor(Math.random() * 11);
            return colors[colorIndex].value;
        },
        generateLabel(obj) {
            const type = obj.type;
            let label = this.capitalizeWords(obj.label);

            switch (type) {
                case 'average':
                    label = 'Average ' + label;
                    break;
                case 'max':
                    label = 'Max ' + label;
                    break;
                case 'min':
                    label = 'Min ' + label;
                    break;
                case 'count_users':
                    label = 'Users Count for ' + label;
                    break;
                case 'count_forms':
                    label = 'Forms Count for ' + label;
                    break;
                case 'sum':
                    label = 'Sum of ' + label;
                    break;
            }

            return label;
        },
        capitalizeWords(word) {
            // Split the word into words
            let words = word.split(' ');

            // Capitalize the first letter of each word
            for (let i = 0; i < words.length; i++) {
                words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
            }

            // Join the words back into a word
            return words.join(' ');
        },
    },
});
